import { reactive, ref, computed, watch } from 'vue';
import { defineStore } from 'pinia';
import { useAtomicSetter } from '@/composables/useAtomicSetter.js';
import { TRANSFERS_DEFAULT_DATE_FIELD } from '@/constants/dateFields.js';

const useTransfersStore = defineStore('transfers', () => {
  const filters = reactive({
    status: [],
    search_text: '',
    sort_by: 'created_at',
    sort_order: 'desc',
    page: 1,
    per_page: 25,
    date: TRANSFERS_DEFAULT_DATE_FIELD,
    date_from: '',
    date_to: '',
    statement_id: '',
    recipient_country: '',
    sender_country: '',
    recipient_currency: '',
    sender_currency: '',
  });

  const shouldResetFilters = ref(true);

  // Kept outside `filters` (spread wholesale into API params) so public_id never leaks into the
  // request. Shared so the active-filter badge can read the label without a redundant fetch.
  const selectedStatement = ref(null);

  const stateRefs = {
    shouldResetFilters,
  };

  const { setAtomic } = useAtomicSetter(stateRefs);

  const setFilters = (newFilters) => {
    Object.assign(filters, newFilters);
  };

  // Clears the structured filters, not search_text - used by the explicit "Clear filters" action.
  const resetAllFilters = () => {
    selectedStatement.value = null;
    Object.assign(filters, {
      page: 1,
      status: [],
      date: TRANSFERS_DEFAULT_DATE_FIELD,
      date_from: '',
      date_to: '',
      statement_id: '',
      recipient_country: '',
      sender_country: '',
      recipient_currency: '',
      sender_currency: '',
    });
  };

  watch(
    () => filters.search_text,
    (newSearchText, oldSearchText) => {
      if (newSearchText !== oldSearchText) {
        setFilters({ page: 1 });
      }
    }
  );

  // Filters auto-apply as soon as they change (no explicit "Apply" step), so any change to
  // one should re-page back to 1, same as a new search term does above.
  watch(
    () => [
      filters.status,
      filters.date,
      filters.date_from,
      filters.date_to,
      filters.statement_id,
      filters.recipient_country,
      filters.sender_country,
      filters.recipient_currency,
      filters.sender_currency,
    ],
    () => setFilters({ page: 1 }),
    { deep: true }
  );

  // Used by useFiltersReset() on a fresh visit - only clears the search box, leaving the
  // sticky structured filters above untouched (those only clear via "Clear filters").
  const resetFilters = () => {
    Object.assign(filters, { search_text: '', page: 1 });
  };

  return { filters, selectedStatement, setFilters, resetFilters, resetAllFilters, ...stateRefs, setAtomic };
});

const useTransferReservationsStore = defineStore('transfer-reservations', () => {
  const filters = reactive({
    status: 'all',
    search_text: '',
    sort_by: 'created_at',
    sort_order: 'desc',
    page: 1,
    per_page: 25,
  });

  const setFilters = (newFilters) => {
    Object.assign(filters, newFilters);
  };

  const resetFilters = () => {
    Object.assign(filters, { page: 1, search_text: '' });
  };

  watch(
    () => filters.search_text,
    (newSearchText, oldSearchText) => {
      if (newSearchText !== oldSearchText) {
        setFilters({ page: 1 });
      }
    }
  );

  return { filters, setFilters, resetFilters };
});

const useTransferDrawerStore = defineStore('transfer-drawer', () => {
  const transferId = ref(null);

  const isOpen = computed(() => !!transferId.value);

  const stateRefs = {
    transferId,
  };

  const { setAtomic } = useAtomicSetter(stateRefs);

  const closeDrawer = () => {
    setAtomic('transferId', null);
  };

  return {
    transferId,
    isOpen,
    setAtomic,
    closeDrawer,
  };
});

const useTransferFiltersDrawerStore = defineStore('transfer-filters-drawer', () => {
  const isOpen = ref(false);

  const openDrawer = () => {
    isOpen.value = true;
  };

  const closeDrawer = () => {
    isOpen.value = false;
  };

  return { isOpen, openDrawer, closeDrawer };
});

export { useTransfersStore, useTransferDrawerStore, useTransferReservationsStore, useTransferFiltersDrawerStore };
